import React from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { TOWNS } from "../../lib/constants";

export const ServiceArea = () => {
  return (
    <section
      id="service-area"
      data-testid="service-area-section"
      className="bg-white py-24 sm:py-32 border-b-2 border-navy"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div data-testid="service-area-text">
            <span className="label-pill">
              <span className="h-1.5 w-1.5 bg-gold" />
              04 — Where we work
            </span>
            <h2 className="font-display uppercase text-navy text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[0.95]">
              Syracuse + <span className="text-gold">50&nbsp;miles</span>
              <br />
              around it.
            </h2>
            <p className="mt-6 font-body text-navy/75 text-base sm:text-lg max-w-xl">
              If you're anywhere in Onondaga, Oswego, Madison, Oneida, or
              Cayuga County — we're close. Here are some of the towns we show
              up in every week:
            </p>

            <ul className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-0 border-2 border-navy">
              {TOWNS.map((t, i) => (
                <li
                  key={t}
                  className={`flex items-center gap-2 px-4 py-3 font-display uppercase tracking-wide text-navy text-sm bg-white ${
                    i % 2 !== 0 ? "" : "bg-offwhite"
                  } ${i < TOWNS.length - 1 ? "border-b border-navy/25" : ""}`}
                  data-testid={`town-${t.toLowerCase()}`}
                >
                  <MapPin className="h-4 w-4 text-gold" strokeWidth={2.5} />
                  {t}
                </li>
              ))}
            </ul>

            <p className="mt-6 text-navy/60 text-sm">
              Outside this area? Call us anyway — we'll make it work when we can.
            </p>
          </div>

          {/* Map graphic */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="service-area-map-image"
            className="relative aspect-square max-w-xl w-full mx-auto border-2 border-navy bg-navy overflow-hidden"
          >
            {/* Grid pattern */}
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
                backgroundSize: "40px 40px",
              }}
            />
            {/* Radius rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              {[0.95, 0.75, 0.55, 0.35].map((s) => (
                <div
                  key={s}
                  className="absolute rounded-full border-2 border-gold/40"
                  style={{
                    width: `${s * 100}%`,
                    height: `${s * 100}%`,
                  }}
                />
              ))}
              <div className="absolute rounded-full border-2 border-gold radar-ping" style={{width:'55%', height:'55%'}} />
            </div>

            {/* Town labels (approx positions) */}
            {[
              { t: "OSWEGO", top: "14%", left: "32%" },
              { t: "FULTON", top: "26%", left: "40%" },
              { t: "BALDWINSVILLE", top: "48%", left: "15%" },
              { t: "LIVERPOOL", top: "42%", left: "42%" },
              { t: "CICERO", top: "36%", left: "62%" },
              { t: "ROME", top: "32%", left: "85%" },
              { t: "UTICA", top: "60%", left: "86%" },
              { t: "CAMILLUS", top: "58%", left: "28%" },
              { t: "AUBURN", top: "75%", left: "18%" },
            ].map((p) => (
              <span
                key={p.t}
                className="absolute -translate-x-1/2 -translate-y-1/2 font-display text-[10px] sm:text-xs uppercase tracking-wider text-white/75"
                style={{ top: p.top, left: p.left }}
              >
                · {p.t}
              </span>
            ))}

            {/* Center: Syracuse */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
              <div className="relative">
                <div className="h-5 w-5 rounded-full bg-gold border-2 border-white" />
                <div className="absolute inset-0 rounded-full bg-gold radar-ping" />
              </div>
              <p className="mt-3 font-display uppercase text-gold text-sm sm:text-base tracking-[0.2em]">
                Syracuse
              </p>
              <p className="mt-1 font-body text-white/80 text-[10px] sm:text-xs uppercase tracking-wider">
                50-mile service radius
              </p>
            </div>

            {/* Corner label */}
            <div className="absolute bottom-4 left-4 label-pill !bg-gold !border-gold !text-navy">
              Base
            </div>
            <div className="absolute bottom-4 right-4 label-pill !bg-transparent !border-white/60 !text-white">
              NY · USA
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
